import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError, formatDateTime } from "@/admin/adminApi";
import { PageHead } from "@/admin/AdminLayout";
import { card, Pill } from "@/admin/ui";
import SeatFreedAlerts from "@/admin/components/SeatFreedAlerts";

function Stat({ label, value, hint, testId }) {
  return (
    <div className={`${card} px-6 py-6`} data-testid={testId}>
      <p className="font-body text-[10.5px] tracking-[0.28em] uppercase text-muted-warm">{label}</p>
      <p className="mt-3 font-serif-display text-[44px] leading-none text-graphite" data-testid={`${testId}-value`}>{value}</p>
      {hint && <p className="mt-3 font-body text-[12.5px] text-graphite-soft">{hint}</p>}
    </div>
  );
}

function UpcomingSessions({ sessions, programs }) {
  if (sessions.length === 0) {
    return (
      <p data-testid="dashboard-no-sessions" className="font-body text-[14.5px] text-graphite-soft">
        Még nincs meghirdetett időpont. Időpontot a{" "}
        <Link to="/admin/programok" className="text-gold border-b border-gold">Programok és időpontok</Link> menüben adhatsz hozzá.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-[#E6E1D8]">
      {sessions.map((s) => (
        <li key={s.id} className="py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`dashboard-session-${s.id}`}>
          <div>
            <p className="font-body text-[14.5px] text-graphite">{programs[s.program_id] || "—"}</p>
            <p className="font-body text-[13px] text-graphite-soft">{formatDateTime(s.starts_at)}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-body text-[13.5px] text-graphite">{s.registered} / {s.capacity} fő</span>
            {s.free === 0 ? <Pill tone="gold">Betelt</Pill> : <Pill tone="petrol">{s.free} szabad hely</Pill>}
            {s.waitlist > 0 && <Pill tone={s.seat_freed_alert ? "gold" : "muted"} testId={`dashboard-waitlist-${s.id}`}>Várólista: {s.waitlist}</Pill>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([adminApi.get("/admin/stats"), adminApi.get("/admin/programs")])
      .then(([s, p]) => {
        setStats(s.data);
        setPrograms(Object.fromEntries(p.data.map((x) => [x.id, x.title])));
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  if (!stats) return <p className="font-body text-graphite-soft">Betöltés…</p>;

  return (
    <div data-testid="admin-dashboard">
      <PageHead eyebrow="Áttekintés" title="Szép napot, Enikő!" />
      <div className="mb-6">
        <SeatFreedAlerts sessions={stats.seat_freed_alerts} programs={programs}
          onPick={(s) => navigate("/admin/jelentkezok", { state: { filters: { program_id: s.program_id, session_id: s.id, status: "waitlist" } } })} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
        <Stat testId="stat-programs" label="Aktuális programok" value={stats.programs_active} hint={`Összesen ${stats.programs_total} program`} />
        <Stat testId="stat-sessions" label="Meghirdetett időpontok" value={stats.sessions_upcoming} hint={`Ebből nyitott: ${stats.sessions_open}`} />
        <Stat testId="stat-registrations" label="Beérkezett jelentkezések" value={stats.registrations_total} hint={`Új: ${stats.registrations_new}`} />
        <Stat testId="stat-paid" label="Befizetett részvételek" value={stats.paid_total} />
        <Stat testId="stat-free" label="Szabad helyek" value={stats.free_seats} hint="A közelgő időpontokon" />
      </div>
      <section className={`${card} mt-8 px-6 md:px-8 py-7`}>
        <h2 className="font-serif-display text-[24px] text-graphite mb-4">Közelgő időpontok</h2>
        <UpcomingSessions sessions={stats.upcoming_sessions} programs={programs} />
      </section>
    </div>
  );
}
